import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/layout/Layout';
import { useAuth } from '../context/AuthContext';
import { PhoneCall, Mic, Brain, Volume2, Clock, User, Building2, Radio } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_MAP = {
  listening: { label: 'In ascolto', color: 'bg-amber-500', icon: Mic, pulse: true },
  processing: { label: 'AI elabora...', color: 'bg-blue-500', icon: Brain, pulse: true },
  speaking: { label: 'AI risponde', color: 'bg-emerald-500', icon: Volume2, pulse: true },
  idle: { label: 'Connesso', color: 'bg-slate-400', icon: Radio, pulse: false },
};

function formatDuration(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function LiveCallCard({ call }) {
  const st = STATUS_MAP[call.status] || STATUS_MAP.idle;
  const StatusIcon = st.icon;
  const [elapsed, setElapsed] = useState(call.duration_seconds || 0);
  const transcriptRef = useRef(null);

  // Live timer
  useEffect(() => {
    setElapsed(call.duration_seconds || 0);
    const interval = setInterval(() => setElapsed(prev => prev + 1), 1000);
    return () => clearInterval(interval);
  }, [call.duration_seconds]);

  // Auto-scroll transcript
  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [call.transcript]);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm" data-testid={`live-call-${call.call_id}`}>
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${st.color} ${st.pulse ? 'animate-pulse' : ''}`}>
            <StatusIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="font-semibold text-slate-800" data-testid="live-call-patient">
                {call.patient_name || 'Paziente sconosciuto'}
              </p>
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full text-white ${st.color}`}>
                {st.label}
              </span>
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
              <span className="flex items-center gap-1"><PhoneCall className="w-3 h-3" />{call.caller_phone || '—'}</span>
              <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{call.clinic_name}</span>
            </div>
          </div>
        </div>
        <div className="text-right">
          <p className="text-2xl font-mono font-bold text-slate-800" data-testid="live-call-timer">{formatDuration(elapsed)}</p>
          <p className="text-[10px] text-slate-400">{call.media_count} pacchetti</p>
        </div>
      </div>

      {/* Transcript */}
      <div ref={transcriptRef} className="px-5 py-3 max-h-64 overflow-y-auto space-y-2 bg-slate-50/50" data-testid="live-call-transcript">
        {(!call.transcript || call.transcript.length === 0) ? (
          <p className="text-xs text-slate-400 text-center py-6">In attesa della conversazione...</p>
        ) : call.transcript.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'assistant' ? 'justify-start' : 'justify-end'}`}>
            <div className={`max-w-[80%] px-3 py-2 rounded-xl text-sm ${
              msg.role === 'assistant'
                ? 'bg-sky-50 text-sky-900 border border-sky-100'
                : msg.role === 'system'
                ? 'bg-red-50 text-red-700 border border-red-100 text-xs'
                : 'bg-white text-slate-800 border border-slate-200'
            }`}>
              <p className="text-[10px] font-medium mb-0.5 opacity-60">
                {msg.role === 'assistant' ? 'AI' : msg.role === 'system' ? 'Sistema' : 'Paziente'}
              </p>
              <p className="leading-relaxed">{msg.content}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function LiveCalls() {
  const { token } = useAuth();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCalls = async () => {
    try {
      const res = await fetch(`${API}/api/voice/live-calls`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCalls(data.calls || []);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCalls();
    const interval = setInterval(fetchCalls, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Layout>
      <div className="p-4 sm:p-8 max-w-5xl" data-testid="live-calls-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Radio className="w-6 h-6 text-red-500" />
              Chiamate Live
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {calls.length > 0
                ? `${calls.length} chiamat${calls.length === 1 ? 'a' : 'e'} in corso`
                : 'Nessuna chiamata in corso'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full ${calls.length > 0 ? 'bg-red-500 animate-pulse' : 'bg-slate-300'}`} />
            <span className="text-xs text-slate-500">Aggiornamento ogni 3s</span>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-slate-400">
            <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            Caricamento...
          </div>
        ) : calls.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-slate-200" data-testid="no-live-calls">
            <PhoneCall className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-lg font-medium text-slate-500">Nessuna chiamata attiva</p>
            <p className="text-sm text-slate-400 mt-1">Quando un paziente chiama, la conversazione apparira' qui in tempo reale</p>
          </div>
        ) : (
          <div className="space-y-4">
            {calls.map(call => (
              <LiveCallCard key={call.call_id} call={call} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
